import { WeekRangePicker } from 'app/components/common/DatePickers';
import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, TableModel } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { useHistoryWithQuery } from 'app/hooks/useHistoryWithQuery';
import { Steart } from 'app/models/core/steart.model';
import { LeadNowConvVM } from 'app/stores/ui/agt.leadnow.conv.ui.store';
import { formatNum, formatPercent, formatPercentWithIcon } from 'app/utils';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { LeadNowConversionFunnelChart } from './LeadNowConversionFunnelChart';
import { Icon } from 'app/components/common/Icon';

export const LeadNowConversionOptions = observer(() => {
	const { agtLeadNowConvUiStore } = useStore();

	const onMinChange = (value: number) => {
		runInAction(() => {
			agtLeadNowConvUiStore.minKwCurrent = value;
		});
	};

	const onMaxChange = (value: number) => {
		runInAction(() => {
			agtLeadNowConvUiStore.maxKwCurrent = value;
		});
	};

	return (
		<WeekRangePicker
			minValue={agtLeadNowConvUiStore.minKw}
			maxValue={agtLeadNowConvUiStore.maxKw}
			opts={agtLeadNowConvUiStore.kwsOpts}
			onMinChange={onMinChange}
			onMaxChange={onMaxChange}
			queryParam="kw"
		/>
	);
});

export const LeadNowConversionListe = observer(() => {
	const { berichteUiiStore, agtLeadNowConvUiStore, agenturListUiStore, session } = useStore();
	const history = useHistoryWithQuery();
	const [hasItems, setHasItems] = useState(false);
	const [funnelItems, setFunnelItems] = useState<LeadNowConvVM[]>([]);

	// Two "Durchschnitt" benchmark lines: the company-wide (ABV) average plus the
	// VD average. LDP and EDT users only see the ABV line.
	const steart = session.currentSteart?.steart;
	const showVd = steart !== Steart.LDP && steart !== Steart.EDT;

	const tm = useMemo(() => {
		const tm = new TableModel<LeadNowConvVM, number>();
		const cols: ITableColumn<LeadNowConvVM, number>[] = [
			{
				label: 'Agentur',
				path: 'agt.name',
				sortBy: 'agt.name',
				render: AgenturNameCell,
			},
			{
				label: 'Brutto',
				path: 'brutto',
				format: formatNum,
				agg: [{ aggType: AggregrationType.sum }],
			},
			{
				label: 'Netto',
				path: 'netto',
				format: formatNum,
				agg: [{ aggType: AggregrationType.sum }],
			},
			{
				label: 'Konvertiert',
				path: 'converted',
				format: formatNum,
				agg: [{ aggType: AggregrationType.sum }],
			},
			{
				label: 'Abschlussquote',
				path: 'conversionRate',
				format: (val: number) => formatPercentWithIcon(val, 1),
				agg: [
					{
						aggType: AggregrationType.avg,
						// weighted overall quote (sum converted / sum netto), matching the funnel chart
						render: (tm: TableModel<LeadNowConvVM, number>) => {
							const netto = tm.data.reduce((sum, d) => sum + d.netto, 0);
							const converted = tm.data.reduce((sum, d) => sum + d.converted, 0);
							const quote = netto ? (converted / netto) * 100 : 0;
							return <>{formatPercentWithIcon(quote, 1)}</>;
						},
					},
				],
			},
			{
				label: 'Neu Stück',
				path: 'neuStck',
				format: formatNum,
				agg: [{ aggType: AggregrationType.sum }],
			},
			{
				label: 'Neu Bewertung',
				path: 'neuBew',
				format: formatNum,
				agg: [{ aggType: AggregrationType.sum }],
			},
		];

		tm.sortBy = 'agt.name';
		tm.setCols(cols);
		tm.idProperty = 'agtId';
		tm.idType = 'number';

		// On the aggregated (non-Agentur) view, clicking a row drills into the
		// Agentur view by setting the agtId query param.
		tm.onRowClick = (row) => {
			if (berichteUiiStore.selectedAgt) {
				return;
			}
			history.pushWithQuery({
				search: '?agtId=' + row.data.agtId,
			});
		};

		return tm;
	}, []);

	useEffect(() => {
		agtLeadNowConvUiStore.load();
	}, [agtLeadNowConvUiStore]);

	useEffect(() => {
		if (agtLeadNowConvUiStore.minKw && agtLeadNowConvUiStore.maxKw) {
			agtLeadNowConvUiStore.aggregateAgtsByKw(agtLeadNowConvUiStore.minKw, agtLeadNowConvUiStore.maxKw);
		}
	}, [agtLeadNowConvUiStore, agtLeadNowConvUiStore.minKw, agtLeadNowConvUiStore.maxKw]);

	useEffect(() => {
		let items = agtLeadNowConvUiStore.items;
		if (items.length === 0) {
			setHasItems(false);
			setFunnelItems([]);
			tm.setRowData([]);
			return;
		}

		if (berichteUiiStore.selectedAgt) {
			const agtId = berichteUiiStore.selectedAgt.agtId;
			items = items.filter((i) => i.agt.agtId === agtId);
		} else {
			items = agenturListUiStore.applyFilterOnAgt(items) as LeadNowConvVM[];
			items = agenturListUiStore.applyFilterOnGpRelevantAgt(items) as LeadNowConvVM[];
		}
		setHasItems(items.length > 0);
		setFunnelItems(items);
		tm.setRowData(items);
		tm.sort();
	}, [tm, berichteUiiStore.selectedAgt, agenturListUiStore.filterHasChanged, agtLeadNowConvUiStore.items]);

	// Load the benchmark totals. The VD anchor is the selected agentur's VD, or the
	// user's own VD on the list view. LDP/EDT don't get a VD average at all.
	useEffect(() => {
		const minKw = agtLeadNowConvUiStore.minKw;
		const maxKw = agtLeadNowConvUiStore.maxKw;
		if (!minKw || !maxKw) {
			return;
		}

		let vdId: string | undefined;
		if (showVd) {
			vdId = berichteUiiStore.selectedAgt ? berichteUiiStore.selectedAgt.vdId : session.currentUser?.vdList?.find((vd) => !!vd);
		}
		agtLeadNowConvUiStore.loadBenchmark(minKw, maxKw, vdId);
	}, [agtLeadNowConvUiStore, session, showVd, berichteUiiStore.selectedAgt, agtLeadNowConvUiStore.minKw, agtLeadNowConvUiStore.maxKw]);

	return (
		<div className="table-container">
			<LeadNowConversionOptions />
			{!hasItems || tm.data.length === 0 ? (
				<div className="pad-1rem">Keine Lead-Conversion-Daten</div>
			) : (
				<>
					<LeadNowConversionFunnelChart
						items={funnelItems}
						averageTotals={agtLeadNowConvUiStore.abvBenchmark}
						averageLabel={'ABV Durchschnitt'}
						secondAverageTotals={showVd ? agtLeadNowConvUiStore.vdBenchmark : undefined}
						secondAverageLabel={'VD Durchschnitt'}
					/>
					<div className="pad-1rem">
						<div className="note-box">
							<p>
								<Icon iconClass="info-circle" />
							</p>
							<div className="note-message">Beinhaltet auch kostenfreie Leads, z. B. aus dem Bestandskundengeschäft oder Service-Leads.</div>
						</div>
					</div>
					<Table stickyHeader={true} tm={tm} stickyFooter={true} />
				</>
			)}
		</div>
	);
});
